import React, { useEffect, useMemo, useState } from 'react';
import { Autocomplete, Grid, Paper, Typography, Box, Avatar, Card, CardContent, Chip, Button, TextField, useMediaQuery, useTheme } from '@mui/material';
import { ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, AreaChart, Area } from 'recharts';
import api from '../api/api';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import BuildIcon from '@mui/icons-material/Build';
import WarningIcon from '@mui/icons-material/Warning';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import HistoryIcon from '@mui/icons-material/History';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import ArchiveIcon from '@mui/icons-material/Archive';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { Snackbar, Alert, IconButton } from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import ConfirmDialog from './ConfirmDialog';
import { getFavoriteTruckIds, getRecentTruckIds, isFavoriteTruck, recordTruckRecent, toggleFavoriteTruck } from '../utils/truckPrefs';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import EventIcon from '@mui/icons-material/Event';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { useAuth } from '../auth/AuthContext';
import { formatPortalDate } from '../utils/dateFormat';
import { DefectReportDialog } from './TruckInspectionLogs';
import DashboardDefectReports from './DashboardDefectReports';
import TruckReminders from './TruckReminders';
import SummaryCard from './SummaryCard';
import { Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { calendarMonthRange, currentMonthValue, TruckScheduleCalendar } from './TruckScheduling';

function greetingForHour(hour) {
  if (hour < 12) return 'Good Morning!';
  if (hour < 17) return 'Good Afternoon!';
  return 'Good Evening!';
}

function localDateForSchedule(days = 0) {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

const dashboardPanelSx = {
  p: { xs: 1.75, sm: 2.25 },
  borderRadius: 3,
  border: '1px solid #e0e7ef',
  boxShadow: '0 4px 18px rgba(16,42,67,0.04)',
  bgcolor: 'white',
  height: '100%',
  minWidth: 0,
};

function tintedDashboardPanel(tint, accent) {
  return {
    ...dashboardPanelSx,
    background: `linear-gradient(155deg, ${tint} 0%, #ffffff 65%)`,
    borderTop: `3px solid ${accent}`,
  };
}

function DashboardSectionHeading({ title, subtitle }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5, mt: 1 }}>
      <Box>
        <Typography sx={{ color: '#16324f', fontWeight: 900, fontSize: '.94rem' }}>{title}</Typography>
        <Typography variant="caption" sx={{ color: '#718196' }}>{subtitle}</Typography>
      </Box>
      <Box sx={{ flex: 1, height: '1px', bgcolor: '#dfe7ef' }} />
    </Box>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, canAccessPath } = useAuth();
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  const [summary, setSummary] = useState({ trucks: 0, parts: 0, lowStock: 0, totalValue: 0 });
  const [chartData, setChartData] = useState([]);
  const [recentMovements, setRecentMovements] = useState([]);
  const [upcomingReminders, setUpcomingReminders] = useState([]);
  const [recentServices, setRecentServices] = useState([]);
  const [serviceTotal, setServiceTotal] = useState(0);
  const [truckNameById, setTruckNameById] = useState(new Map());
  const [partById, setPartById] = useState(new Map());
  const [trucks, setTrucks] = useState([]);
  const [reminderVehiclePickerOpen, setReminderVehiclePickerOpen] = useState(false);
  const [reminderVehicle, setReminderVehicle] = useState(null);
  const [reminderEditorVehicle, setReminderEditorVehicle] = useState(null);
  const [prefsTick, setPrefsTick] = useState(0);
  const [toast, setToast] = useState({ open: false, severity: 'success', message: '' });
  const [archiveConfirm, setArchiveConfirm] = useState({ open: false, truck: null });
  const [archiving, setArchiving] = useState(false);
  const [exportingMaster, setExportingMaster] = useState(false);
  const [onRoadVehicles, setOnRoadVehicles] = useState([]);
  const [onRoadRefreshedAt, setOnRoadRefreshedAt] = useState(null);
  const [onRoadLoading, setOnRoadLoading] = useState(true);
  const [onRoadError, setOnRoadError] = useState('');
  const [gloveBoxExpiries, setGloveBoxExpiries] = useState([]);
  const [gpsLocations, setGpsLocations] = useState([]);
  const [truckSchedules, setTruckSchedules] = useState([]);
  const [scheduleCalendarMonth, setScheduleCalendarMonth] = useState(currentMonthValue);
  const [scheduleCalendarAssignments, setScheduleCalendarAssignments] = useState([]);
  const [defectTruckId, setDefectTruckId] = useState('');
  const [defectOpen, setDefectOpen] = useState(false);
  const canSeeTruckScheduling = canAccessPath('/truck-scheduling');
  const canReportDefect = canAccessPath('/drivers');

  useEffect(() => {
    async function load() {
      try {
        const [t, p, m, reminders, services, gps, schedules, gloveBox] = await Promise.all([
          api.get('/trucks').catch(() => ({ data: [] })),
          api.get('/parts').catch(() => ({ data: [] })),
          api.get('/part-movements/recent?limit=25').catch(() => ({ data: [] })),
          api.get('/notifications/reminders', { params: { lookAheadDays: 30 } }).catch(() => ({ data: [] })),
          api.get('/service-logs', { params: { page: 0, pageSize: 5, sortBy: 'service_date', sortDir: 'desc' } }).catch(() => ({ data: { items: [], total: 0 } })),
          api.get('/gps/locations', { params: { limit: 8 } }).catch(() => ({ data: [] })),
          canSeeTruckScheduling
            ? api.get('/truck-schedules', { params: { from: localDateForSchedule(0), to: localDateForSchedule(7) } }).catch(() => ({ data: [] }))
            : Promise.resolve({ data: [] }),
          api.get('/dashboard/glove-box-expiries').catch(() => ({ data: { documents: [] } })),
        ]);

        const totalValue = (p.data || []).reduce((sum, part) => sum + (parseFloat(part.current_value) || 0), 0);
        setSummary({
          trucks: (t.data || []).length,
          parts: (p.data || []).length,
          lowStock: (p.data || []).filter((part) => Number(part.quantity ?? 0) < Number(part.low_stock_threshold ?? 2)).length,
          totalValue
        });

        const byYear = {};
        (p.data || []).forEach(item => {
          const y = item.year || 'Unknown';
          byYear[y] = (byYear[y] || 0) + 1;
        });
        const data = Object.keys(byYear).sort().map(year => ({ year, count: byYear[year] }));
        setChartData(data);

        const truckMap = new Map();
        (t.data || []).forEach((truck) => {
          truckMap.set(truck.id, truck.name || `${truck.make || ''} ${truck.model || ''}`.trim() || `Truck ${truck.id}`);
        });
        setTruckNameById(truckMap);
        setTrucks(t.data || []);

        const partMap = new Map();
        (p.data || []).forEach((part) => {
          partMap.set(part.id, part);
        });
        setPartById(partMap);

        setRecentMovements(Array.isArray(m.data) ? m.data : []);
        setUpcomingReminders(Array.isArray(reminders.data) ? reminders.data : []);
        setRecentServices(Array.isArray(services.data?.items) ? services.data.items : []);
        setServiceTotal(Number(services.data?.total || 0));
        setGpsLocations(Array.isArray(gps.data) ? gps.data : []);
        setTruckSchedules(Array.isArray(schedules.data) ? schedules.data : []);
        setGloveBoxExpiries(Array.isArray(gloveBox.data?.documents) ? gloveBox.data.documents : []);
      } catch (err) {
        console.error(err);
      }
    }
    load();

    const handler = () => load();
    window.addEventListener('dataUpdated', handler);
    return () => window.removeEventListener('dataUpdated', handler);
  }, [canSeeTruckScheduling]);

  useEffect(() => {
    if (!canSeeTruckScheduling) {
      setScheduleCalendarAssignments([]);
      return undefined;
    }
    let active = true;
    const loadCalendar = async () => {
      try {
        const range = calendarMonthRange(scheduleCalendarMonth);
        const response = await api.get('/truck-schedules', { params: { from: range.from, to: range.to } });
        if (active) setScheduleCalendarAssignments((Array.isArray(response.data) ? response.data : []).filter((assignment) => assignment.status !== 'cancelled'));
      } catch (error) {
        console.error('Error loading dashboard truck schedule calendar:', error);
      }
    };
    loadCalendar();
    const refresh = () => document.visibilityState === 'visible' && loadCalendar();
    const interval = window.setInterval(refresh, 30_000);
    window.addEventListener('focus', refresh);
    window.addEventListener('dataUpdated', refresh);
    return () => {
      active = false;
      window.clearInterval(interval);
      window.removeEventListener('focus', refresh);
      window.removeEventListener('dataUpdated', refresh);
    };
  }, [canSeeTruckScheduling, scheduleCalendarMonth]);

  useEffect(() => {
    let active = true;
    const refreshOnRoad = async () => {
      if (active) setOnRoadLoading(true);
      try {
        const response = await api.get('/dashboard/on-road', {
          params: { refresh: Date.now() },
          headers: { 'Cache-Control': 'no-cache' },
        });
        if (!active) return;
        setOnRoadVehicles(Array.isArray(response.data?.vehicles) ? response.data.vehicles : []);
        setOnRoadRefreshedAt(response.data?.refreshed_at || new Date().toISOString());
        setOnRoadError('');
      } catch (error) {
        console.error('Error loading on-road vehicles:', error);
        if (active) {
          setOnRoadError(error?.response?.data?.error || 'Could not load vehicles currently on the road.');
        }
      } finally {
        if (active) setOnRoadLoading(false);
      }
    };
    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible') refreshOnRoad();
    };
    refreshOnRoad();
    const interval = window.setInterval(refreshOnRoad, 30_000);
    window.addEventListener('focus', refreshOnRoad);
    window.addEventListener('refreshOnRoadVehicles', refreshOnRoad);
    document.addEventListener('visibilitychange', refreshWhenVisible);
    return () => {
      active = false;
      window.clearInterval(interval);
      window.removeEventListener('focus', refreshOnRoad);
      window.removeEventListener('refreshOnRoadVehicles', refreshOnRoad);
      document.removeEventListener('visibilitychange', refreshWhenVisible);
    };
  }, []);

  useEffect(() => {
    const handler = () => setPrefsTick((v) => v + 1);
    window.addEventListener('prefsUpdated', handler);
    return () => window.removeEventListener('prefsUpdated', handler);
  }, []);

  const getTruckName = (id, fallback = 'Unassigned') => {
    if (!id) return fallback;
    return truckNameById.get(id) || `Truck #${id}`;
  };

  const favoriteIds = getFavoriteTruckIds();
  const favoriteTrucks = trucks.filter((t) => favoriteIds.includes(t.id)).slice(0, 8);
  const recentIds = getRecentTruckIds(8);
  const recentTrucks = recentIds.map((id) => trucks.find((t) => t.id === id)).filter(Boolean);
  const firstName = String(user?.name || user?.email || 'there').trim().split(/\s+/)[0] || 'there';
  const timeGreeting = greetingForHour(new Date().getHours());
  const selectedDefectTruck = trucks.find((truck) => Number(truck.id) === Number(defectTruckId)) || null;
  const calendarReminders = useMemo(() => (
    upcomingReminders
      .filter((item) => item.due_date)
      .sort((a, b) => String(a.due_date).localeCompare(String(b.due_date)))
      .slice(0, 8)
  ), [upcomingReminders]);
  const dueNowCount = upcomingReminders.filter((item) => item.due_reason !== 'upcoming_date').length;
  const upcomingOnlyCount = upcomingReminders.filter((item) => item.due_reason === 'upcoming_date').length;
  const gloveBoxExpiryVehicles = useMemo(() => {
    const grouped = new Map();
    gloveBoxExpiries.forEach((document) => {
      const truckId = Number(document.truck_id);
      if (!grouped.has(truckId)) {
        grouped.set(truckId, {
          truck_id: truckId,
          truck_name: document.truck_name || [document.year, document.make, document.model].filter(Boolean).join(' ') || `Vehicle #${truckId}`,
          license_plate: document.license_plate,
          documents: [],
        });
      }
      grouped.get(truckId).documents.push(document);
    });
    return Array.from(grouped.values());
  }, [gloveBoxExpiries]);

  const formatShortDate = (value) => {
    if (!value) return '—';
    const d = new Date(`${String(value).slice(0, 10)}T00:00:00`);
    if (Number.isNaN(d.getTime())) return '—';
    return formatPortalDate(d);
  };

  const handleToggleFavorite = (truckId) => {
    const wasFavorite = isFavoriteTruck(truckId);
    toggleFavoriteTruck(truckId);
    setPrefsTick((v) => v + 1);
    setToast({ open: true, severity: 'success', message: wasFavorite ? 'Removed from favorites.' : 'Added to favorites.' });
  };

  const requestArchive = (truck) => setArchiveConfirm({ open: true, truck });

  const confirmArchive = async () => {
    if (!archiveConfirm.truck?.id) return;
    try {
      setArchiving(true);
      const endpoint = archiveConfirm.truck.archived ? 'unarchive' : 'archive';
      const res = await api.put(`/trucks/${archiveConfirm.truck.id}/${endpoint}`);
      const archivedPartCount = Number(res.data?.archived_parts_count || 0);
      window.dispatchEvent(new Event('vehiclesUpdated'));
      window.dispatchEvent(new Event('partsUpdated'));
      window.dispatchEvent(new Event('dataUpdated'));
      setToast({
        open: true,
        severity: 'success',
        message: archiveConfirm.truck.archived
          ? 'Truck unarchived.'
          : archivedPartCount
            ? `Truck archived with ${archivedPartCount} attached part record${archivedPartCount === 1 ? '' : 's'}.`
            : 'Truck archived.',
      });
    } catch (err) {
      console.error(err);
      const message = err?.response?.data?.error || err?.message || 'Archive failed.';
      setToast({ open: true, severity: 'error', message });
    } finally {
      setArchiving(false);
      setArchiveConfirm({ open: false, truck: null });
    }
  };

  const handleExportMaster = async () => {
    try {
      setExportingMaster(true);
      const res = await api.get('/export/master', { responseType: 'blob', timeout: 120000 });

      const disposition = res.headers?.['content-disposition'] || '';
      const match = String(disposition).match(/filename="?([^";]+)"?/i);
      const filename = match?.[1] || 'biofleet_master_export.zip';

      const blob = new Blob([res.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setToast({ open: true, severity: 'success', message: 'Master export downloaded.' });
    } catch (err) {
      console.error('Error exporting master backup:', err);
      const message = err?.response?.data?.error || err?.message || 'Master export failed.';
      setToast({ open: true, severity: 'error', message });
    } finally {
      setExportingMaster(false);
    }
  };

  const getMovementChipSx = (type) => {
    const t = String(type || '').toLowerCase();
    if (t === 'removed') return { backgroundColor: '#ffebee', color: '#c62828' };
    if (t === 'assigned') return { backgroundColor: '#e3f2fd', color: '#1565c0' };
    if (t === 'unassigned') return { backgroundColor: '#eeeeee', color: '#424242' };
    if (t === 'installed') return { backgroundColor: '#e8f5e9', color: '#2e7d32' };
    if (t === 'transferred') return { backgroundColor: '#fff3e0', color: '#e65100' };
    return { backgroundColor: '#eeeeee', color: '#424242' };
  };

  const summaryItems = [
    {
      label: 'Fleet Vehicles',
      value: summary.trucks,
      accent: '#866523', tint: '#f9dfa4',
      icon: <DirectionsCarIcon sx={{ fontSize: 32 }} />,
      to: '/trucks',
    },
    {
      label: 'Total Parts',
      value: summary.parts,
      accent: '#356d59', tint: '#c4e8d1',
      icon: <BuildIcon sx={{ fontSize: 32 }} />,
      to: '/parts',
    },
    {
      label: 'Inventory Value',
      value: `$${summary.totalValue.toLocaleString()}`,
      accent: '#865b77', tint: '#f3cde2',
      icon: <TrendingUpIcon sx={{ fontSize: 32 }} />
    },
    {
      label: 'Drivers',
      value: 'Check-In',
      accent: '#655b8c', tint: '#d9ccf3',
      icon: <PeopleAltIcon sx={{ fontSize: 32 }} />,
      to: '/drivers',
    },
    {
      label: 'Service Logs',
      value: 'Logs',
      accent: '#3b658b', tint: '#c6dff7',
      icon: <BuildIcon sx={{ fontSize: 32 }} />,
      to: '/service-logs',
    },
    {
      label: 'Inventory Management',
      value: 'Inventory',
      accent: '#367372', tint: '#bfe5df',
      icon: <Inventory2Icon sx={{ fontSize: 32 }} />,
      to: '/inventory',
    },
    {
      label: 'User Management',
      value: 'Users',
      accent: '#775e8b', tint: '#e4cef1',
      icon: <PeopleAltIcon sx={{ fontSize: 32 }} />,
      to: '/users',
    },
    {
      label: 'Truck Scheduling',
      value: `${truckSchedules.filter((item) => item.status !== 'cancelled').length} Runs`,
      accent: '#546f46', tint: '#d3e6b5',
      icon: <EventIcon sx={{ fontSize: 32 }} />,
      to: '/truck-scheduling',
    },
  ];

  return (
    <Box sx={{
      minHeight: '100dvh',
      width: '100%',
      p: { xs: 1.5, sm: 2, lg: 2.5 },
      boxSizing: 'border-box',
    }}>
      <Card sx={{
        mb: 2,
        background: 'radial-gradient(ellipse at 90% 0%, rgba(57,154,155,.3), transparent 55%), linear-gradient(115deg, #06253f 0%, #0b4269 65%, #134e67 100%)',
        color: 'white',
        borderRadius: 3,
        border: '1px solid rgba(255,255,255,.22)',
        boxShadow: '0 10px 28px rgba(6,37,63,0.16)'
      }}>
        <CardContent sx={{ p: { xs: 1.75, sm: 2, md: 2.25 }, '&:last-child': { pb: { xs: 1.75, sm: 2, md: 2.25 } } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.25 }, flexWrap: 'wrap' }}>
            <Avatar sx={{
              width: { xs: 40, sm: 46 },
              height: { xs: 40, sm: 46 },
              background: 'linear-gradient(135deg, #00e69a, #00bd80)',
              borderRadius: 2,
            }}>
              <TrendingUpIcon sx={{ fontSize: { xs: 23, sm: 27 }, color: '#001f3f' }} />
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h3" sx={{
                fontWeight: 900,
                mb: 0.1,
                fontSize: { xs: '1rem', sm: '1.18rem', md: '1.3rem' },
                lineHeight: 1.05,
                letterSpacing: '-0.2px'
              }}>
                Hi {firstName}
              </Typography>
              <Typography variant="h6" sx={{
                opacity: 0.85,
                fontWeight: 700,
                fontSize: { xs: '0.68rem', sm: '0.76rem' },
                lineHeight: 1.25
              }}>
                {timeGreeting} • Fleet overview
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<FileDownloadIcon />}
              onClick={handleExportMaster}
              disabled={exportingMaster}
              sx={{
                ml: { xs: 0, md: 'auto' },
                bgcolor: '#ffffff',
                color: '#001f3f',
                fontWeight: 600,
                borderRadius: 1.6,
                px: 1.40,
                py: 0.52,
                minHeight: 36,
                fontSize: { xs: '0.70rem', sm: '0.76rem' },
                boxShadow: '0 5px 16px rgba(0,0,0,0.16)',
                '&:hover': {
                  bgcolor: '#e8f2ff',
                  boxShadow: '0 10px 28px rgba(0,0,0,0.24)',
                },
                '&.Mui-disabled': {
                  bgcolor: 'rgba(255,255,255,0.7)',
                  color: 'rgba(0,31,63,0.55)',
                },
              }}
            >
              {exportingMaster ? 'Exporting...' : 'Master Export'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      <DashboardSectionHeading title="Overview & quick access" subtitle="Fleet totals and your workspace shortcuts" />
      <Grid container spacing={1} sx={{ mb: { xs: 2, md: 2.5 } }}>
        {summaryItems.filter((item) => !item.to || canAccessPath(item.to)).map((item) => (
          <Grid size={{ xs: 6, md: 3, xl: 1.5 }} key={item.label}>
            <SummaryCard {...item} component={item.to ? Link : undefined} />
          </Grid>
        ))}
      </Grid>

      <DashboardSectionHeading title="Live operations & document alerts" subtitle="On-road vehicles and documents needing attention" />

      <Paper
        sx={{
          mb: { xs: 1.5, md: 2 },
          p: { xs: 1.5, sm: 2 },
          borderRadius: 3,
          border: onRoadVehicles.length ? '1px solid rgba(0, 168, 107, 0.28)' : '1px solid #e6ebf2',
          background: onRoadVehicles.length
            ? 'linear-gradient(135deg, rgba(232,248,241,0.98) 0%, rgba(245,252,249,0.98) 100%)'
            : 'white',
          boxShadow: onRoadVehicles.length ? '0 10px 30px rgba(0, 127, 91, 0.10)' : '0 8px 24px rgba(16, 42, 67, 0.05)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap', mb: onRoadVehicles.length ? 1.5 : 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <Box sx={{ position: 'relative', width: 42, height: 42, borderRadius: 2, display: 'grid', placeItems: 'center', bgcolor: onRoadVehicles.length ? '#087f5b' : '#eef2f6', color: onRoadVehicles.length ? 'white' : '#607080' }}>
              <DirectionsCarIcon />
              {onRoadVehicles.length > 0 && (
                <Box sx={{ position: 'absolute', right: -3, top: -3, width: 12, height: 12, borderRadius: '50%', bgcolor: '#29d391', border: '2px solid white', boxShadow: '0 0 0 4px rgba(41,211,145,0.15)' }} />
              )}
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 900, color: '#001f3f', lineHeight: 1.2 }}>Vehicles On the Road</Typography>
              <Typography variant="caption" color="text.secondary">
                Pre-trip completed and awaiting post-trip{onRoadRefreshedAt ? ` • Live as of ${formatPortalDate(onRoadRefreshedAt, { includeTime: true })}` : ''}
              </Typography>
            </Box>
          </Box>
          <Chip
            label={onRoadLoading ? 'Refreshing…' : `${onRoadVehicles.length} on the road`}
            color={onRoadVehicles.length ? 'success' : 'default'}
            sx={{ fontWeight: 900, px: 0.5 }}
          />
        </Box>

        {onRoadError ? (
          <Alert
            severity="warning"
            action={<Button color="inherit" size="small" onClick={() => window.dispatchEvent(new Event('refreshOnRoadVehicles'))}>Retry</Button>}
            sx={{ ml: { sm: 6.7 } }}
          >
            {onRoadError}
          </Alert>
        ) : onRoadVehicles.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ ml: { sm: 6.7 } }}>No vehicles are currently marked on the road.</Typography>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr))' }, gap: 1 }}>
            {onRoadVehicles.map((vehicle) => (
              <Paper
                key={vehicle.truck_id}
                component="button"
                type="button"
                onClick={() => navigate(`/truck/${vehicle.truck_id}`)}
                variant="outlined"
                sx={{
                  p: 1.35,
                  borderRadius: 2.25,
                  textAlign: 'left',
                  bgcolor: 'white',
                  borderColor: 'rgba(0,127,91,0.22)',
                  cursor: 'pointer',
                  font: 'inherit',
                  transition: 'transform 150ms ease, box-shadow 150ms ease',
                  '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 8px 20px rgba(0,127,91,0.12)' },
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 900, color: '#001f3f', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {vehicle.name || [vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(' ') || `Vehicle #${vehicle.truck_id}`}
                    </Typography>
                    <Typography sx={{ display: 'block', mt: 0.25, color: '#16324f', fontSize: { xs: '0.86rem', sm: '0.9rem' }, fontWeight: 800 }}>
                      {vehicle.license_plate || 'No plate'} • Driver: {vehicle.inspected_by || 'Not recorded'}
                    </Typography>
                    {vehicle.other_driver_name && (
                      <Typography sx={{ display: 'block', mt: 0.15, color: '#35506b', fontSize: { xs: '0.82rem', sm: '0.86rem' }, fontWeight: 800 }}>
                        Other Driver: {vehicle.other_driver_name}
                      </Typography>
                    )}
                    <Typography variant="caption" sx={{ display: 'block', mt: 0.4, color: '#087f5b', fontWeight: 800 }}>
                      Pre-trip: {formatPortalDate(vehicle.occurred_at, { includeTime: true, preserveWallTime: true })}
                    </Typography>
                  </Box>
                  <Chip size="small" label="ON ROAD" color="success" sx={{ fontWeight: 900, flexShrink: 0 }} />
                </Box>
              </Paper>
            ))}
          </Box>
        )}
      </Paper>

      <Paper
        sx={{
          mb: { xs: 1.5, md: 2 },
          p: { xs: 1.5, sm: 2 },
          borderRadius: 3,
          border: gloveBoxExpiries.length ? '1px solid #d8e4ee' : '1px solid #e6ebf2',
          background: gloveBoxExpiries.length
            ? 'linear-gradient(135deg, #f8fbfd 0%, #f5f8fb 100%)'
            : 'white',
          boxShadow: gloveBoxExpiries.length ? '0 10px 28px rgba(16, 42, 67, 0.07)' : '0 8px 24px rgba(16, 42, 67, 0.05)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap', mb: gloveBoxExpiries.length ? 1.5 : 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <Box sx={{ width: 42, height: 42, borderRadius: 2, display: 'grid', placeItems: 'center', bgcolor: gloveBoxExpiries.length ? '#e7f0f7' : '#eef2f6', color: gloveBoxExpiries.length ? '#24506f' : '#607080', border: '1px solid #d4e2ec' }}>
              <DescriptionOutlinedIcon />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 900, color: '#001f3f', lineHeight: 1.2 }}>Glove Box Documents Expiring Soon</Typography>
              <Typography variant="caption" color="text.secondary">Documents already expired or expiring within the next 7 days</Typography>
            </Box>
          </Box>
          <Chip
            label={`${gloveBoxExpiries.length} document${gloveBoxExpiries.length === 1 ? '' : 's'}`}
            sx={{ fontWeight: 900, px: 0.5, bgcolor: gloveBoxExpiries.length ? '#e8f1f7' : '#eef2f6', color: gloveBoxExpiries.length ? '#365a73' : '#607080' }}
          />
        </Box>

        {gloveBoxExpiryVehicles.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ ml: { sm: 6.7 } }}>No Glove Box documents are expired or expiring within a week.</Typography>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(3, minmax(0, 1fr))' }, gap: 1 }}>
            {gloveBoxExpiryVehicles.map((vehicle, vehicleIndex) => {
              const vehicleThemes = [
                { dark: '#315f7d', light: '#eaf3f8', border: '#cfe0ea' },
                { dark: '#426b5d', light: '#edf5f1', border: '#d5e6de' },
                { dark: '#625b78', light: '#f1eff5', border: '#dfdbe8' },
                { dark: '#775d65', light: '#f6f0f2', border: '#e8dade' },
              ];
              const vehicleTheme = vehicleThemes[vehicleIndex % vehicleThemes.length];
              return (
                <Paper
                  key={vehicle.truck_id}
                  component="div"
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate(`/truck/${vehicle.truck_id}?tab=gloveBox`)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      navigate(`/truck/${vehicle.truck_id}?tab=gloveBox`);
                    }
                  }}
                  variant="outlined"
                  sx={{
                    p: 0,
                    display: 'block',
                    alignSelf: 'stretch',
                    height: '100%',
                    borderRadius: 2.5,
                    textAlign: 'left',
                    bgcolor: 'rgba(255,255,255,0.96)',
                    borderColor: vehicleTheme.border,
                    cursor: 'pointer',
                    font: 'inherit',
                    overflow: 'hidden',
                    transition: 'transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease',
                    '&:hover': { transform: 'translateY(-3px)', borderColor: vehicleTheme.dark, boxShadow: '0 12px 26px rgba(16,42,67,0.10)' },
                  }}
                >
                  <Box sx={{ p: 1.25, background: `linear-gradient(135deg, ${vehicleTheme.light} 0%, #ffffff 145%)`, color: vehicleTheme.dark, borderBottom: `1px solid ${vehicleTheme.border}` }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.05 }}>
                      <Avatar sx={{ width: 38, height: 38, bgcolor: 'rgba(255,255,255,0.78)', color: vehicleTheme.dark, border: `1px solid ${vehicleTheme.border}` }}>
                        <DirectionsCarIcon sx={{ fontSize: 21 }} />
                      </Avatar>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography sx={{ textTransform: 'uppercase', letterSpacing: 1.1, fontSize: '0.62rem', lineHeight: 1.1, fontWeight: 900, opacity: 0.7 }}>Vehicle</Typography>
                        <Typography sx={{ mt: 0.2, fontWeight: 950, fontSize: '1rem', lineHeight: 1.25, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{vehicle.truck_name}</Typography>
                      </Box>
                      <Box sx={{ px: 1, py: 0.45, borderRadius: 99, bgcolor: 'rgba(255,255,255,0.82)', border: `1px solid ${vehicleTheme.border}`, flexShrink: 0 }}>
                        <Typography sx={{ fontSize: '0.68rem', fontWeight: 900 }}>{vehicle.license_plate || 'NO PLATE'}</Typography>
                      </Box>
                    </Box>
                  </Box>

                  <Box sx={{ p: 1.15 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.8 }}>
                      <Typography sx={{ color: vehicleTheme.dark, fontSize: '0.72rem', fontWeight: 950, textTransform: 'uppercase', letterSpacing: 0.6 }}>Glove Box Documents</Typography>
                      <Chip size="small" label={`${vehicle.documents.length} alert${vehicle.documents.length === 1 ? '' : 's'}`} sx={{ height: 23, fontWeight: 900, bgcolor: vehicleTheme.light, color: vehicleTheme.dark }} />
                    </Box>
                    <Box sx={{ display: 'grid', gap: 0.65 }}>
                      {vehicle.documents.map((document) => {
                        const expired = Number(document.days_until_expiry) < 0;
                        const expiresToday = Number(document.days_until_expiry) === 0;
                        const statusLabel = expired
                          ? `Expired ${Math.abs(Number(document.days_until_expiry))} day${Math.abs(Number(document.days_until_expiry)) === 1 ? '' : 's'} ago`
                          : expiresToday
                            ? 'Expires today'
                            : `Expires in ${document.days_until_expiry} day${Number(document.days_until_expiry) === 1 ? '' : 's'}`;
                        return (
                          <Box key={document.document_id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, p: 0.85, borderRadius: 1.7, bgcolor: expired ? '#fff1f2' : '#fffbeb', border: `1px solid ${expired ? '#fecdd3' : '#fde68a'}` }}>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="body2" sx={{ fontWeight: 850, color: '#27364a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{document.title || 'Untitled document'}</Typography>
                              <Typography variant="caption" sx={{ color: '#64748b' }}>Expires {formatShortDate(document.expires_at)}</Typography>
                            </Box>
                            <Chip size="small" label={statusLabel} sx={{ flexShrink: 0, maxWidth: { xs: 132, sm: 'none' }, fontWeight: 850, bgcolor: expired || expiresToday ? '#fee2e2' : '#fef3c7', color: expired || expiresToday ? '#b91c1c' : '#92400e' }} />
                          </Box>
                        );
                      })}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.45, mt: 1, color: vehicleTheme.dark }}>
                      <Typography sx={{ fontSize: '0.7rem', fontWeight: 900 }}>Open vehicle Glove Box</Typography>
                      <ArrowForwardIosIcon sx={{ fontSize: 11 }} />
                    </Box>
                  </Box>
                </Paper>
              );
            })}
          </Box>
        )}
      </Paper>

      {(canSeeTruckScheduling || canReportDefect) && (
        <Box sx={{ mb: { xs: 2, md: 2.5 } }}>
        <DashboardSectionHeading title="Planning & defect reports" subtitle="Schedule runs, report issues, and review defect history" />
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: canSeeTruckScheduling && canReportDefect ? 'repeat(2, minmax(0, 1fr))' : '1fr' }, gap: 2, alignItems: 'start' }}>
          {canSeeTruckScheduling && (
            <Box sx={{ minWidth: 0 }}>
              <TruckScheduleCalendar
                compact
                assignments={scheduleCalendarAssignments}
                monthValue={scheduleCalendarMonth}
                onMonthChange={setScheduleCalendarMonth}
                onOpenAssignment={() => navigate('/truck-scheduling')}
                onOpenCalendar={() => navigate('/truck-scheduling')}
              />
              <Paper sx={{ ...dashboardPanelSx, height: 'auto', mt: 2 }}>
                <Typography sx={{ fontWeight: 900, color: '#16324f', mb: 0.5 }}>Driver schedule notifications</Typography>
                <Typography variant="caption" sx={{ color: '#718196', display: 'block', mb: 1.5 }}>
                  Received means the driver’s signed-in app loaded the schedule, not that they read it. Push status is tracked separately.
                </Typography>
                {scheduleCalendarAssignments.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">No schedules for this month.</Typography>
                ) : (
                  <Box sx={{ maxHeight: 300, overflowY: 'auto', display: 'grid', gap: 1 }}>
                    {scheduleCalendarAssignments.map((assignment) => (
                      <Box key={assignment.id} sx={{ p: 1.25, borderRadius: 2, border: '1px solid #e1e9ef', bgcolor: assignment.notification_received_at ? '#f0f7f3' : '#f7f9fc' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 0.75 }}>
                          <Typography sx={{ fontWeight: 800, fontSize: '.85rem', color: '#16324f' }}>
                            {assignment.driver_name || 'Driver'} {assignment.notification_received_at ? 'received notification in app' : '— awaiting app receipt'}
                          </Typography>
                          <Chip size="small" label={assignment.notification_received_at ? 'Received in app' : 'Pending'} sx={{ bgcolor: assignment.notification_received_at ? '#dfeee5' : '#e9eef4', color: assignment.notification_received_at ? '#35634b' : '#526579', fontWeight: 700 }} />
                        </Box>
                        <Typography variant="caption" sx={{ display: 'block', color: '#64748b', mt: 0.5 }}>
                          {assignment.vehicle_name} • {assignment.destination} • {assignment.run_start_date}
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', color: '#64748b', mt: 0.35 }}>
                          Push: {({ accepted: 'Accepted by push provider', disabled: 'Push not configured', not_sent: 'Not sent — no successful device delivery', failed: 'Send failed', pending: 'Pending / not yet tracked' })[assignment.notification_push_status] || 'Not yet tracked'}
                          {assignment.notification_received_at ? ` • App receipt: ${formatPortalDate(assignment.notification_received_at, { includeTime: true })}` : ''}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </Paper>
            </Box>
          )}

          {canReportDefect && (
            <Card sx={{ borderRadius: 3, overflow: 'hidden', border: '1px solid #ead7e1', borderTop: '3px solid #c498ae', boxShadow: '0 4px 18px rgba(91,52,75,.05)', background: 'linear-gradient(155deg, #fcf5f8, #ffffff 65%)' }}>
              <Box sx={{ p: { xs: 1.5, sm: 1.8 }, color: '#70445b', background: 'linear-gradient(135deg, #f3e0e9 0%, #faf0f5 100%)', borderBottom: '1px solid #ead7e1' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.15 }}>
                  <Avatar sx={{ width: 42, height: 42, borderRadius: 2, bgcolor: '#ebcfdd', color: '#87516d', border: '1px solid #dfbace' }}><WarningIcon /></Avatar>
                  <Box><Typography sx={{ fontWeight: 950, fontSize: '1.08rem' }}>Defect Reports</Typography><Typography sx={{ opacity: .86, fontSize: '.76rem' }}>Report an issue and review previous defects</Typography></Box>
                </Box>
              </Box>
              <CardContent sx={{ p: { xs: 1.5, sm: 1.8 }, '&:last-child': { pb: { xs: 1.5, sm: 1.8 } } }}>
                <Box sx={{ p: 1.5, borderRadius: 2.5, bgcolor: '#fcf3f7', border: '1px solid #eddae5' }}>
                <Typography sx={{ fontSize: '.72rem', fontWeight: 900, letterSpacing: .65, textTransform: 'uppercase', color: '#87516d', mb: .6 }}>New defect report</Typography>
                <Typography variant="body2" sx={{ color: '#64748b', mb: 1.5 }}>
                  Choose a vehicle to report an issue, add notes, or attach photos.
                </Typography>
                <Autocomplete
                  fullWidth
                  options={trucks.filter((truck) => truck.archived !== true)}
                  value={selectedDefectTruck}
                  onChange={(_event, truck) => setDefectTruckId(truck?.id || '')}
                  isOptionEqualToValue={(option, value) => Number(option.id) === Number(value.id)}
                  getOptionLabel={(truck) => [truck.name, truck.license_plate, [truck.year, truck.make, truck.model].filter(Boolean).join(' ')].filter(Boolean).join(' • ')}
                  renderInput={(params) => <TextField {...params} label="Vehicle" placeholder="Search vehicle or plate" />}
                  sx={{ mb: 1.5, bgcolor: 'white' }}
                />
                <Button fullWidth variant="contained" startIcon={<WarningIcon />} disabled={!selectedDefectTruck} onClick={() => setDefectOpen(true)} sx={{ py: 1, borderRadius: 2, bgcolor: '#87516d', fontWeight: 900, boxShadow: '0 4px 12px rgba(91,52,75,.13)', '&:hover': { bgcolor: '#70445b' } }}>
                  Report Defect
                </Button>
                <Typography variant="caption" sx={{ display: 'block', mt: 1, textAlign: 'center', color: '#64748b' }}>
                  Selected email recipients are notified after submission.
                </Typography>
                </Box>
                <DashboardDefectReports />
              </CardContent>
            </Card>
          )}
        </Box>
        </Box>
      )}

      <DashboardSectionHeading title="Activity & insights" subtitle="Inventory trends, reminders, and recent fleet activity" />
      <Grid container spacing={{ xs: 1.5, md: 2 }} alignItems="stretch">
        <Grid size={{ xs: 12, md: 5 }}>
          <Paper sx={{ ...tintedDashboardPanel('#edf4fc', '#8eb4da'), minHeight: { md: 420 } }}>
            <Typography variant="h6" sx={{
              mb: 2,
              fontWeight: 700,
              color: '#001f3f',
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              <TrendingUpIcon color="primary" /> Parts Distribution by Year
            </Typography>
            <ResponsiveContainer width="100%" height={isPhone ? 230 : 285}>
              <AreaChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 10 }}>
                <defs>
                  <linearGradient id="colorCount" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#1976d2" stopOpacity={0.5} />
                    <stop offset="95%" stopColor="#1976d2" stopOpacity={0.04} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis
                  dataKey="year"
                  style={{ fontSize: '0.85rem' }}
                  tick={{ fill: '#666' }}
                />
                <YAxis
                  allowDecimals={false}
                  style={{ fontSize: '0.85rem' }}
                  tick={{ fill: '#666' }}
                />
                <Tooltip
                  contentStyle={{
                    borderRadius: 12,
                    border: 'none',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="count"
                  stroke="#1976d2"
                  strokeWidth={3}
                  fill="url(#colorCount)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 7 }}>
          <Paper sx={{ ...tintedDashboardPanel('#edf7f5', '#88bcb0'), minHeight: { md: 420 }, display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6" sx={{
              mb: 2,
              fontWeight: 700,
              color: '#001f3f',
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              <HistoryIcon color="primary" /> Recent Inventory Transfers
            </Typography>
            <Box sx={{ mb: 2 }}>
              <Button
                variant="outlined"
                size="small"
                href="#/movements"
                fullWidth={isPhone}
              >
                View all history
              </Button>
            </Box>

            {recentMovements.length === 0 ? (
              <Typography variant="body2" sx={{ color: '#777', fontStyle: 'italic' }}>
                No inventory transfer history yet.
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, overflow: 'auto', maxHeight: { xs: 360, md: 285 }, pr: 0.5 }}>
                {recentMovements.slice(0, 6).map((m) => {
                  const part = partById.get(m.part_id);
                  return (
                    <Box
                      key={m.id}
                      sx={{
                        p: 1.25,
                        borderRadius: 3,
                        border: '1px solid #e3e8ff',
                        background: 'linear-gradient(135deg, #fafcff 0%, #ffffff 100%)',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: '#001f3f' }}>
                            {part ? `${part.sku} — ${part.part_type}` : `Part #${m.part_id}`}
                          </Typography>
                          <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                            {m.created_at ? formatPortalDate(m.created_at, { includeTime: true }) : ''}
                          </Typography>
                        </Box>
                        <Chip
                          size="small"
                          label={(m.movement_type || 'movement').toString().toUpperCase()}
                          sx={{ ...getMovementChipSx(m.movement_type), fontWeight: 'bold' }}
                        />
                      </Box>

                      <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <SwapHorizIcon sx={{ fontSize: 18, color: '#5c6bc0' }} />
                        <Typography variant="caption" color="textSecondary">
                          {m.from_location || getTruckName(m.from_truck_id, 'Shelf')} → {m.to_location || getTruckName(m.to_truck_id)}
                        </Typography>
                        {m.moved_by && (
                          <Typography variant="caption" color="textSecondary">
                            • By: {m.moved_by}
                          </Typography>
                        )}
                      </Box>

                      {m.notes && (
                        <Typography variant="body2" sx={{ mt: 0.75, fontStyle: 'italic', color: '#333' }}>
                          {m.notes}
                        </Typography>
                      )}
                    </Box>
                  );
                })}
              </Box>
            )}
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={tintedDashboardPanel('#f4effb', '#b4a0ce')}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'flex-start', mb: 2, flexWrap: 'wrap' }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 800, color: '#001f3f', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <EventIcon color="primary" /> Reminder Calendar
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Due now + next 30 days
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {(canAccessPath('/trucks') || canAccessPath('/service-logs')) && (
                  <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={() => { setReminderVehicle(null); setReminderVehiclePickerOpen(true); }} sx={{ bgcolor: '#75649a', borderRadius: 2, fontWeight: 800, '&:hover': { bgcolor: '#625184' } }}>
                    Add Reminder
                  </Button>
                )}
                <Chip size="small" sx={{ bgcolor: '#fff0f1', color: '#a3424f', fontWeight: 800 }} label={`${dueNowCount} Due`} />
                <Chip size="small" sx={{ bgcolor: '#edf3f9', color: '#365c82', fontWeight: 800 }} label={`${upcomingOnlyCount} Upcoming`} />
              </Box>
            </Box>
            {calendarReminders.length === 0 ? (
              <Typography variant="body2" sx={{ color: '#777', fontStyle: 'italic' }}>
                No date-based reminders in the next 30 days.
              </Typography>
            ) : (
              <Box sx={{ display: 'grid', gap: 1 }}>
                {calendarReminders.map((item) => (
                  <Box
                    key={item.id}
                    onClick={() => item.truck_id && navigate(`/truck/${item.truck_id}`)}
                    sx={{
                      p: 1.35,
                      borderRadius: 3,
                      border: '1px solid #dbeafe',
                      bgcolor: item.due_reason === 'upcoming_date' ? '#f8fbff' : '#fff5f5',
                      cursor: item.truck_id ? 'pointer' : 'default',
                      display: 'grid',
                      gridTemplateColumns: '104px minmax(0, 1fr)',
                      gap: 1.25,
                    }}
                  >
                    <Box sx={{ borderRadius: 2, bgcolor: item.due_reason === 'upcoming_date' ? '#e3f2fd' : '#ffebee', textAlign: 'center', py: 0.8, fontWeight: 900, color: item.due_reason === 'upcoming_date' ? '#1565c0' : '#c62828' }}>
                      {formatShortDate(item.due_date)}
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 800, color: '#001f3f', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {item.title}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {item.truck_name || 'Fleet'} • {item.priority || 'None'}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={tintedDashboardPanel('#edf7ef', '#90bd9b')}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'flex-start', mb: 2, flexWrap: 'wrap' }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 800, color: '#001f3f', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AssignmentTurnedInIcon color="success" /> Completed Services
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Latest completed service logs
                </Typography>
              </Box>
              <Chip size="small" sx={{ bgcolor: '#edf5f1', color: '#426b5d', fontWeight: 800 }} label={`${serviceTotal} Total`} />
            </Box>
            {recentServices.length === 0 ? (
              <Typography variant="body2" sx={{ color: '#777', fontStyle: 'italic' }}>
                No completed services recorded yet.
              </Typography>
            ) : (
              <Box sx={{ display: 'grid', gap: 1 }}>
                {recentServices.map((wrapped) => {
                  const service = wrapped?.service || wrapped;
                  const truck = wrapped?.truck || null;
                  return (
                    <Box
                      key={service.id}
                      onClick={() => service.truck_id && navigate(`/truck/${service.truck_id}`)}
                      sx={{ p: 1.35, borderRadius: 3, border: '1px solid #dcfce7', bgcolor: '#fbfffd', cursor: service.truck_id ? 'pointer' : 'default' }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 800, color: '#001f3f' }}>
                        {service.service_done || 'Service completed'}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {truck?.name || getTruckName(service.truck_id, 'Vehicle')} • {formatShortDate(service.service_date)} • {service.performed_by || '—'}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Paper sx={tintedDashboardPanel('#fff7e9', '#d5b579')}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 800, color: '#001f3f', display: 'flex', alignItems: 'center', gap: 1 }}>
              <StarIcon sx={{ color: '#fbc02d' }} /> Favorite Vehicles
            </Typography>
            {favoriteTrucks.length === 0 ? (
              <Typography variant="body2" sx={{ color: '#777', fontStyle: 'italic' }}>
                No favorites yet. Star a vehicle to pin it.
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {favoriteTrucks.map((t) => (
                  <Box
                    key={`favdash-${t.id}-${prefsTick}`}
                    sx={{ p: 1.5, borderRadius: 3, border: '1px solid #e3e8ff', background: '#fafcff', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}
                  >
                    <Box sx={{ minWidth: 0, cursor: 'pointer' }} onClick={() => { recordTruckRecent(t.id); navigate(`/truck/${t.id}`); }}>
                      <Typography variant="body2" sx={{ fontWeight: 800, color: '#001f3f', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {t.name || `${t.make || ''} ${t.model || ''}`.trim()}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {t.license_plate || '—'} • {t.status || 'Active'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <IconButton size="small" onClick={() => handleToggleFavorite(t.id)} sx={{ color: '#fbc02d' }}>
                        <StarIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => requestArchive(t)} sx={{ color: '#546e7a' }}>
                        <ArchiveIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Paper sx={tintedDashboardPanel('#edf4fc', '#8eb4da')}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 800, color: '#001f3f', display: 'flex', alignItems: 'center', gap: 1 }}>
              <HistoryIcon color="primary" /> Recent Vehicles
            </Typography>
            {recentTrucks.length === 0 ? (
              <Typography variant="body2" sx={{ color: '#777', fontStyle: 'italic' }}>
                Recently edited vehicles will appear here.
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {recentTrucks.map((t) => (
                  <Box
                    key={`recdash-${t.id}-${prefsTick}`}
                    sx={{ p: 1.5, borderRadius: 3, border: '1px solid #e3e8ff', background: '#fafcff', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}
                  >
                    <Box sx={{ minWidth: 0, cursor: 'pointer' }} onClick={() => { recordTruckRecent(t.id); navigate(`/truck/${t.id}`); }}>
                      <Typography variant="body2" sx={{ fontWeight: 800, color: '#001f3f', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {t.name || `${t.make || ''} ${t.model || ''}`.trim()}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {t.license_plate || '—'} • {t.status || 'Active'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <IconButton
                        size="small"
                        onClick={() => handleToggleFavorite(t.id)}
                        sx={{ color: isFavoriteTruck(t.id) ? '#fbc02d' : '#90a4ae' }}
                      >
                        {isFavoriteTruck(t.id) ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
                      </IconButton>
                      <IconButton size="small" onClick={() => requestArchive(t)} sx={{ color: '#546e7a' }}>
                        {t.archived ? <UnarchiveIcon fontSize="small" /> : <ArchiveIcon fontSize="small" />}
                      </IconButton>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Paper sx={tintedDashboardPanel('#edf7f5', '#88bcb0')}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 800, color: '#001f3f', display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocationOnIcon sx={{ color: '#00897b' }} /> OneStepGPS Live Locations
            </Typography>
            {gpsLocations.length === 0 ? (
              <Typography variant="body2" sx={{ color: '#777', fontStyle: 'italic' }}>
                No OneStepGPS locations received yet.
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {gpsLocations.map((location) => {
                  const lat = Number(location.latitude);
                  const lng = Number(location.longitude);
                  const mapUrl = Number.isFinite(lat) && Number.isFinite(lng)
                    ? `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`
                    : null;
                  return (
                    <Box
                      key={location.id}
                      sx={{ p: 1.5, borderRadius: 3, border: '1px solid #cdeee8', background: '#f4fffd' }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 900, color: '#001f3f' }}>
                        {location.truck_name || location.vehicle_name || 'Unmatched GPS unit'}
                      </Typography>
                      <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                        {location.truck_license_plate || location.license_plate || location.device_id || 'No identifier'} • {formatPortalDate(location.event_time, { includeTime: true })}
                      </Typography>
                      <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {location.truck_id && (
                          <Button size="small" variant="outlined" onClick={() => navigate(`/truck/${location.truck_id}`)} sx={{ borderRadius: 2, fontWeight: 800 }}>
                            Vehicle
                          </Button>
                        )}
                        {mapUrl && (
                          <Button size="small" variant="contained" href={mapUrl} target="_blank" rel="noreferrer" sx={{ borderRadius: 2, bgcolor: '#00897b', fontWeight: 800 }}>
                            Map
                          </Button>
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      <DefectReportDialog
        open={defectOpen}
        onClose={() => setDefectOpen(false)}
        truckId={selectedDefectTruck ? Number(selectedDefectTruck.id) : null}
        onSaved={() => {
          setDefectOpen(false);
          setToast({ open: true, severity: 'success', message: 'Defect report saved successfully.' });
          window.dispatchEvent(new Event('dataUpdated'));
        }}
      />

      <Dialog open={reminderVehiclePickerOpen} onClose={() => setReminderVehiclePickerOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ color: '#655b8c', fontWeight: 900 }}>Add Calendar Reminder</DialogTitle>
        <DialogContent sx={{ pt: '12px !important' }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Choose a vehicle, then add its reminder date, recurrence, notes, and optional attachments.</Typography>
          <Autocomplete
            options={trucks.filter((truck) => !truck.archived)}
            value={reminderVehicle}
            onChange={(_event, vehicle) => setReminderVehicle(vehicle)}
            getOptionLabel={(vehicle) => [vehicle.name, vehicle.license_plate].filter(Boolean).join(' • ')}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => <TextField {...params} label="Vehicle" placeholder="Search vehicle or plate" />}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setReminderVehiclePickerOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={!reminderVehicle} onClick={() => { setReminderEditorVehicle(reminderVehicle); setReminderVehiclePickerOpen(false); }}>Continue</Button>
        </DialogActions>
      </Dialog>
      {reminderEditorVehicle && (
        <TruckReminders createOnly truckId={reminderEditorVehicle.id} truck={reminderEditorVehicle}
          onClose={() => setReminderEditorVehicle(null)}
          onSaved={() => { setReminderEditorVehicle(null); setToast({ open: true, severity: 'success', message: 'Reminder added successfully.' }); }} />
      )}
      <Snackbar
        open={toast.open}
        autoHideDuration={4500}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          severity={toast.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>

      <ConfirmDialog
        open={archiveConfirm.open}
        title={archiveConfirm.truck?.archived ? 'Unarchive Truck?' : 'Archive Truck?'}
        description={archiveConfirm.truck?.archived
          ? 'This will bring the truck back into your primary lists.'
          : 'This will hide the truck from your primary lists, but keep it in the database.'}
        confirmText={archiving ? (archiveConfirm.truck?.archived ? 'Unarchiving…' : 'Archiving…') : (archiveConfirm.truck?.archived ? 'Unarchive' : 'Archive')}
        confirmColor="warning"
        onConfirm={confirmArchive}
        onClose={() => setArchiveConfirm({ open: false, truck: null })}
        loading={archiving}
        icon={archiveConfirm.truck?.archived ? <UnarchiveIcon /> : <ArchiveIcon />}
      />
    </Box>
  );
}
